import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api, { formatApiError } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import CallQueues from "@/components/CallQueues";
import SalesforceFieldMapping from "@/components/SalesforceFieldMapping";
import CallingUpsellCard from "@/components/CallingUpsellCard";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Trash, FloppyDisk, CheckCircle, UserPlus, Crown, ShieldCheck, PhoneIncoming, Cloud, PlugsConnected, Plugs, ArrowSquareOut, Download, Copy, ArrowClockwise, UploadSimple } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { timezoneList, browserTimezone } from "@/lib/datetime";
import { DIAL_COUNTRIES } from "@/lib/countries";

const PROVIDER_FIELDS = {
    twilio: ["account_sid", "auth_token", "api_key_sid", "api_key_secret", "twiml_app_sid", "voice_number"],
    plivo: ["auth_id", "auth_token", "voice_number"],
    exotel: ["api_key", "api_token", "account_sid", "subdomain", "voice_number", "voip_domain", "voip_proxy", "voip_username", "voip_password"],
    mock: [],
};

const MASK = "•••";
const DAYS = [["mon", "Mon"], ["tue", "Tue"], ["wed", "Wed"], ["thu", "Thu"], ["fri", "Fri"], ["sat", "Sat"], ["sun", "Sun"]];
const DEFAULT_BH = { enabled: false, open: "09:00", close: "18:00", days: ["mon", "tue", "wed", "thu", "fri"], closed_message: "" };

export default function Settings() {
    const { user: currentUser, setOrgTimezone } = useAuth();
    const callCenter = (currentUser?.features?.call_center) !== false;  // WhatsApp-Standalone hides telephony
    const [tz, setTz] = useState("");
    const [searchParams, setSearchParams] = useSearchParams();
    const requestedTab = searchParams.get("tab");
    // Telephony tabs don't exist for WhatsApp-Standalone workspaces.
    const HIDDEN_FOR_STANDALONE = ["provider", "caller-ids", "queues"];
    const defaultTab = callCenter ? "provider" : "team";
    let activeTab = requestedTab || defaultTab;
    if (!callCenter && HIDDEN_FOR_STANDALONE.includes(activeTab)) activeTab = defaultTab;
    const changeTab = (v) => setSearchParams((p) => { p.set("tab", v); return p; }, { replace: true });
    const [tzBusy, setTzBusy] = useState(false);
    const [country, setCountry] = useState("91");
    const [countryBusy, setCountryBusy] = useState(false);
    const [bh, setBh] = useState(DEFAULT_BH);
    const [bhBusy, setBhBusy] = useState(false);
    const [settings, setSettings] = useState(null);
    const [providerName, setProviderName] = useState("mock");
    const [providerCreds, setProviderCreds] = useState({});
    const [callerIds, setCallerIds] = useState([]);
    const [agents, setAgents] = useState([]);
    const [newAgent, setNewAgent] = useState({ name: "", email: "", role: "agent" });
    const [busyAgent, setBusyAgent] = useState(false);
    const [inviteResult, setInviteResult] = useState(null);
    const [seatInfo, setSeatInfo] = useState(null);
    const [sf, setSf] = useState(null);
    const callbackUrl = `${window.location.origin}/salesforce/oauth/callback`;
    const [sfForm, setSfForm] = useState({ client_id: "", client_secret: "", env: "production", domain: "" });
    const [sfBusy, setSfBusy] = useState(false);
    const [presence, setPresence] = useState([]);
    const [routing, setRouting] = useState({ strategy: "simultaneous", ring_timeout: 25 });
    const [routingBusy, setRoutingBusy] = useState(false);
    const [sfMappings, setSfMappings] = useState([]);
    const [sfMapBusy, setSfMapBusy] = useState(false);
    const [sfMapDraft, setSfMapDraft] = useState({});

    const loadSfMappings = async (refresh = false) => {
        try {
            const { data } = await api.get(`/integrations/salesforce/user-mappings${refresh ? "?refresh=1" : ""}`);
            setSfMappings(data.mappings || []);
            const draft = {};
            (data.mappings || []).forEach((m) => { draft[m.user_id] = m.sf_user_email || ""; });
            setSfMapDraft(draft);
            if (refresh) {
                const n = (data.mappings || []).filter((m) => m.matched).length;
                toast.success(`Users refreshed — ${n} matched in the connected org`);
            }
        } catch (_e) {
            if (refresh) toast.error("Could not refresh users");
        }
    };

    const refreshSfUsers = async () => {
        setSfMapBusy(true);
        try { await loadSfMappings(true); } finally { setSfMapBusy(false); }
    };

    const saveSfMapping = async (userId) => {
        setSfMapBusy(true);
        try {
            const { data } = await api.put(`/integrations/salesforce/user-mappings/${userId}`, {
                sf_user_email: sfMapDraft[userId] || "",
            });
            toast[data.matched ? "success" : "error"](
                data.matched ? `Mapped to ${data.sf_user_name}` : "No active Salesforce user found for that email"
            );
            await loadSfMappings();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setSfMapBusy(false);
        }
    };

    const loadSf = async () => {
        try {
            const { data } = await api.get("/integrations/salesforce");
            setSf(data);
            setSfForm((f) => ({ ...f, env: data.env || "production", domain: data.domain || "" }));
            if (data.connected) loadSfMappings();
        } catch (_e) {}
    };

    const saveSfConfig = async () => {
        setSfBusy(true);
        try {
            const payload = { env: sfForm.env, domain: sfForm.domain || "", log_calls: sf?.log_calls ?? true };
            if (sfForm.client_id) payload.client_id = sfForm.client_id;
            if (sfForm.client_secret) payload.client_secret = sfForm.client_secret;
            await api.post("/integrations/salesforce/config", payload);
            setSfForm((f) => ({ ...f, client_id: "", client_secret: "" }));
            await loadSf();
            toast.success("Salesforce app keys saved");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setSfBusy(false);
        }
    };

    const connectSf = async () => {
        try {
            // Land the OAuth redirect on THIS frontend origin's SPA route (works in prod
            // regardless of how /api is routed), then the callback page finishes via XHR.
            const redirectUri = `${window.location.origin}/salesforce/oauth/callback`;
            const { data } = await api.get("/integrations/salesforce/connect", { params: { redirect_uri: redirectUri } });
            const popup = window.open(data.authorize_url, "sf_oauth", "width=620,height=720");
            let done = false;
            const finalize = async () => {
                if (done) return;
                done = true;
                window.removeEventListener("message", handler);
                clearInterval(poll);
                clearTimeout(guard);
                // Decide success from the ACTUAL backend connection status rather than
                // the popup's postMessage — Salesforce's iframe (Locker) can drop the
                // message, which made a successful connect wrongly show as failed.
                try {
                    const { data: st } = await api.get("/integrations/salesforce");
                    setSf(st);
                    setSfForm((f) => ({ ...f, env: st.env || "production", domain: st.domain || "" }));
                    if (st.connected) {
                        toast.success("Salesforce connected successfully");
                        loadSfMappings();
                    } else {
                        toast.error("Salesforce connection failed. Check your keys and callback URL.");
                    }
                } catch (_e) {
                    toast.error("Salesforce connection failed. Check your keys and callback URL.");
                }
            };
            const handler = (e) => {
                if (e.data?.source !== "inspiro-sf") return;
                finalize();
            };
            window.addEventListener("message", handler);
            // Fallback: re-check status when the popup closes without messaging.
            const poll = setInterval(() => {
                if (popup && popup.closed) finalize();
            }, 1000);
            // Safety net if the popup neither messages nor reports closed (blocked).
            const guard = setTimeout(finalize, 120000);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const disconnectSf = async () => {
        try {
            await api.post("/integrations/salesforce/disconnect");
            await loadSf();
            toast.success("Salesforce disconnected");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const testSf = async () => {
        setSfBusy(true);
        try {
            const { data } = await api.post("/integrations/salesforce/test");
            toast.success(data.sf_org_name ? `Connected to ${data.sf_org_name}` : `Connected to org ${data.sf_org_id || data.instance_url}`);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setSfBusy(false);
        }
    };

    const toggleSfLog = async (v) => {
        try {
            await api.post("/integrations/salesforce/config", { env: sf?.env || "production", log_calls: v });
            await loadSf();
            toast.success(v ? "Call logging enabled" : "Call logging disabled");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const load = async () => {
        const { data } = await api.get("/settings");
        setSettings(data);
        setTz(data.timezone || "");
        setCountry(data.default_country_code || "91");
        setBh({ ...DEFAULT_BH, ...(data.business_hours || {}) });
        setCallerIds(data.caller_ids || []);
        const p = (data.providers || []).find((x) => x.name === data.active_provider);
        setProviderName(data.active_provider);
        setProviderCreds(p?.credentials || {});
    };

    const loadAgents = async () => {
        try {
            const { data } = await api.get("/users");
            setAgents(data);
        } catch (_e) {}
        try {
            const { data } = await api.get("/agents/presence");
            setPresence(data);
        } catch (_e) {}
        try {
            const { data } = await api.get("/billing");
            setSeatInfo({ user_count: data.user_count, user_cap: data.user_cap, plan_name: data.current_plan_meta?.name || data.plan });
        } catch (_e) {}
    };

    const loadRouting = async () => {
        try {
            const { data } = await api.get("/settings/routing");
            setRouting(data);
        } catch (_e) {}
    };

    const saveRouting = async () => {
        setRoutingBusy(true);
        try {
            await api.put("/settings/routing", routing);
            toast.success("Call routing updated");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setRoutingBusy(false);
        }
    };

    useEffect(() => {
        load();
        loadAgents();
        loadSf();
        loadRouting();
        const params = new URLSearchParams(window.location.search);
        const sfStatus = params.get("sf");
        if (sfStatus === "connected") {
            toast.success("Salesforce connected successfully");
            loadSf();
            window.history.replaceState({}, "", window.location.pathname);
        } else if (sfStatus === "error") {
            toast.error("Salesforce connection failed. Check your keys and callback URL.");
            window.history.replaceState({}, "", window.location.pathname);
        }
    }, []);

    const saveActiveProvider = async () => {
        try {
            await api.patch("/settings", { active_provider: providerName });
            await load();
            toast.success(`Active provider set to ${providerName}`);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const saveProviderCreds = async () => {
        // Strip mask values — backend will keep existing for any field we omit.
        const stripped = {};
        for (const [k, v] of Object.entries(providerCreds)) {
            if (v && v !== MASK) stripped[k] = v;
        }
        try {
            await api.post("/settings/providers", {
                name: providerName,
                credentials: stripped,
                enabled: true,
            });
            await load();
            toast.success("Provider credentials saved");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const toggleRecording = async (v) => {
        try {
            await api.patch("/settings", { recording_enabled: v });
            await load();
            toast.success(v ? "Outbound recording enabled" : "Outbound recording disabled");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const toggleInboundRecording = async (v) => {
        try {
            await api.patch("/settings", { inbound_recording_enabled: v });
            await load();
            toast.success(v ? "Inbound recording enabled" : "Inbound recording disabled");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const saveCountry = async () => {
        setCountryBusy(true);
        try {
            await api.patch("/settings", { default_country_code: country });
            await load();
            toast.success("Default dialing country updated");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setCountryBusy(false);
        }
    };

    const saveTimezone = async () => {
        setTzBusy(true);
        try {
            // "Auto" (empty) must still persist a concrete IANA zone, otherwise the
            // backend falls back to UTC and business-hours are evaluated in the wrong
            // timezone (e.g. an IST office wrongly reported "closed").
            const resolvedTz = tz || browserTimezone();
            await api.patch("/settings", { timezone: resolvedTz });
            setTz(resolvedTz);
            setOrgTimezone(resolvedTz);
            await load();
            toast.success("Timezone updated");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setTzBusy(false);
        }
    };

    const toggleBhDay = (d) => setBh((b) => ({ ...b, days: b.days.includes(d) ? b.days.filter((x) => x !== d) : [...b.days, d] }));
    const saveBusinessHours = async () => {
        setBhBusy(true);
        try {
            // Business hours are only correct with a concrete workspace timezone; if the
            // org is still on "Auto" (empty), persist the resolved zone alongside so the
            // backend never falls back to UTC.
            const payload = tz ? { business_hours: bh } : { business_hours: bh, timezone: browserTimezone() };
            if (!tz) setTz(browserTimezone());
            await api.patch("/settings", payload);
            await load();
            toast.success("Business hours saved");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setBhBusy(false);
        }
    };

    const uploadSfLogo = (file) => {
        if (!file) return;
        if (!file.type?.startsWith("image/")) { toast.error("Please choose an image file"); return; }
        if (file.size > 150 * 1024) { toast.error("Logo must be under 150 KB"); return; }
        const reader = new FileReader();
        reader.onload = async () => {
            try {
                await api.patch("/settings", { sf_logo: reader.result });
                await load();
                toast.success("Logo updated");
            } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        };
        reader.readAsDataURL(file);
    };

    const resetSfLogo = async () => {
        try {
            await api.patch("/settings", { sf_logo: "" });
            await load();
            toast.success("Logo reset to default");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const onProviderChange = (name) => {
        setProviderName(name);
        const p = (settings?.providers || []).find((x) => x.name === name);
        setProviderCreds(p?.credentials || {});
    };

    const addCallerId = () =>
        setCallerIds((c) => [...c, { label: "", number: "", is_default: c.length === 0 }]);

    const updateCaller = (i, k, v) =>
        setCallerIds((c) => c.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));

    const removeCaller = (i) => setCallerIds((c) => c.filter((_, idx) => idx !== i));

    const setDefaultCaller = (i) =>
        setCallerIds((c) => c.map((x, idx) => ({ ...x, is_default: idx === i })));

    const saveCallerIds = async () => {
        try {
            await api.patch("/settings", { caller_ids: callerIds });
            await load();
            toast.success("Caller IDs saved");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const createAgent = async (e) => {
        e.preventDefault();
        if (!newAgent.email || !newAgent.name) return;
        setBusyAgent(true);
        try {
            const { data } = await api.post("/users", { name: newAgent.name, email: newAgent.email, role: newAgent.role });
            setInviteResult({ link: data.invite_link, email_sent: data.email_sent, email_error: data.email_error, name: newAgent.name, email: newAgent.email });
            toast.success(data.email_sent ? `Invitation emailed to ${newAgent.email}` : "Invite created — copy the link below to share");
            setNewAgent({ name: "", email: "", role: "agent" });
            await loadAgents();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed to send invite");
        } finally {
            setBusyAgent(false);
        }
    };

    const resendInvite = async (member) => {
        try {
            const { data } = await api.post(`/users/${member.id}/resend-invite`);
            setInviteResult({ link: data.invite_link, email_sent: data.email_sent, email_error: data.email_error, name: member.name, email: member.email });
            toast.success(data.email_sent ? `Invitation re-sent to ${member.email}` : "Fresh invite link ready — copy below to share");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed to resend invite");
        }
    };

    const copyInvite = (link) => {
        Promise.resolve(navigator.clipboard?.writeText(link)).catch(() => {});
        toast.success("Invite link copied");
    };

    const downloadCallCenterXml = async () => {
        try {
            const res = await api.get("/integrations/salesforce/call-center.xml", { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([res.data], { type: "application/xml" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = "inspiro-call-center.xml";
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            toast.success("Call Center XML downloaded");
        } catch (e) {
            let msg = "Could not download the Call Center XML";
            try {
                if (e.response?.data instanceof Blob) {
                    msg = JSON.parse(await e.response.data.text()).detail || msg;
                } else {
                    msg = formatApiError(e.response?.data?.detail) || msg;
                }
            } catch (_) {}
            toast.error(msg);
        }
    };

    const changeRole = async (id, role) => {
        try {
            await api.patch(`/users/${id}`, { role });
            toast.success("Role updated");
            await loadAgents();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed to update role");
        }
    };

    const removeMember = async (member) => {
        if (!window.confirm(`Remove ${member.name} from your workspace? This cannot be undone.`)) return;
        try {
            await api.delete(`/users/${member.id}`);
            toast.success(`${member.name} removed`);
            await loadAgents();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed to remove member");
        }
    };

    if (!settings) {
        return (
            <AppShell>
                <div className="p-12 text-sm text-stone2">Loading settings…</div>
            </AppShell>
        );
    }

    const _apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
    const _oid = currentUser?.org_id || "";
    const incomingUrl = `${_apiBase}/api/webhooks/twilio/incoming?org_id=${_oid}`;
    const outboundVoiceUrl = `${_apiBase}/api/webhooks/twilio/voice`;
    const plivoAnswerUrl = `${_apiBase}/api/webhooks/plivo/browser-answer?org_id=${_oid}`;
    const exotelIncomingUrl = `${_apiBase}/api/webhooks/exotel/incoming?org_id=${_oid}`;


    return (
        <AppShell>
            <div className="mx-auto max-w-5xl px-6 py-8 md:px-12 md:py-12">
                <div className="mb-8">
                    <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-stone2">Admin</div>
                    <h1 className="mt-2 font-heading text-4xl font-bold tracking-tight sm:text-5xl">Workspace Settings</h1>
                    <p className="mt-2 text-sm text-stone2">{callCenter ? "Configure telephony providers, caller IDs, team and recording." : "Manage your team, Salesforce connection and workspace preferences."}</p>
                </div>

                <Tabs value={activeTab} onValueChange={changeTab} className="w-full">
                    <TabsList className={`mb-6 grid w-full rounded-full bg-stone-100 p-1 ${callCenter ? "grid-cols-6" : "grid-cols-4"}`}>
                        {callCenter && (
                            <TabsTrigger value="provider" data-testid="tab-provider" className="rounded-full data-[state=active]:bg-ink data-[state=active]:text-white">
                                Provider
                            </TabsTrigger>
                        )}
                        {callCenter && (
                            <TabsTrigger value="caller-ids" data-testid="tab-caller-ids" className="rounded-full data-[state=active]:bg-ink data-[state=active]:text-white">
                                Caller IDs
                            </TabsTrigger>
                        )}
                        {!callCenter && (
                            <TabsTrigger value="add-calling" data-testid="tab-add-calling" className="rounded-full text-brand data-[state=active]:bg-brand data-[state=active]:text-white">
                                Add Calling
                            </TabsTrigger>
                        )}
                        <TabsTrigger value="team" data-testid="tab-team" className="rounded-full data-[state=active]:bg-ink data-[state=active]:text-white">
                            Team
                        </TabsTrigger>
                        {callCenter && (
                            <TabsTrigger value="queues" data-testid="tab-queues" className="rounded-full data-[state=active]:bg-ink data-[state=active]:text-white">
                                Queues
                            </TabsTrigger>
                        )}
                        <TabsTrigger value="salesforce" data-testid="tab-salesforce" className="rounded-full data-[state=active]:bg-ink data-[state=active]:text-white">
                            Salesforce
                        </TabsTrigger>
                        <TabsTrigger value="general" data-testid="tab-general" className="rounded-full data-[state=active]:bg-ink data-[state=active]:text-white">
                            General
                        </TabsTrigger>
                    </TabsList>

                    {!callCenter && (
                        <TabsContent value="add-calling">
                            <CallingUpsellCard variant="panel" />
                        </TabsContent>
                    )}

                    {/* Provider */}
                    <TabsContent value="provider">
                        <div className="space-y-6 rounded-2xl border border-stone-200 bg-white p-6 sm:p-8">
                            <div>
                                <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-stone2">Active Provider</div>
                                <p className="mt-1 text-sm text-stone2">
                                    Switch the telephony provider used for outbound calls. The core dialer is provider-agnostic.
                                </p>

                                <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                                    {(settings.available_providers || []).map((p) => (
                                        <button
                                            key={p}
                                            data-testid={`provider-${p}`}
                                            onClick={() => onProviderChange(p)}
                                            className={`rounded-xl border px-4 py-3 text-left transition-all ${
                                                providerName === p
                                                    ? "border-ink bg-ink text-white"
                                                    : "border-stone-200 bg-white hover:border-ink"
                                            }`}
                                        >
                                            <div className="font-heading text-base font-semibold capitalize">{p}</div>
                                            <div className={`text-[10px] uppercase tracking-widest ${providerName === p ? "text-white/70" : "text-stone2"}`}>
                                                {p === "mock" ? "for testing" : "real provider"}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                                <div className="mt-5 flex items-center gap-3">
                                    <button
                                        data-testid="save-active-provider"
                                        onClick={saveActiveProvider}
                                        className="inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 font-heading text-sm font-semibold text-white hover:-translate-y-0.5 hover:bg-brand-hover"
                                    >
                                        <CheckCircle size={16} weight="fill" /> Set as active
                                    </button>
                                    {settings.active_provider === providerName && (
                                        <span className="text-xs font-bold uppercase tracking-widest text-emerald-600">Currently active</span>
                                    )}
                                </div>
                            </div>

                            <div className="border-t border-stone-200 pt-6">
                                <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-stone2">
                                    Credentials · {providerName}
                                </div>
                                {(PROVIDER_FIELDS[providerName] || []).length === 0 ? (
                                    <p className="mt-3 text-sm text-stone2">Mock provider needs no credentials.</p>
                                ) : (
                                    <>
                                        <p className="mt-1 mb-3 text-xs text-stone2">
                                            Existing values are masked. Type a new value to replace; leave a field as <code>•••</code> to keep current.
                                        </p>
                                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                            {PROVIDER_FIELDS[providerName].map((f) => (
                                                <div key={f}>
                                                    <label className="text-xs font-bold uppercase tracking-widest text-stone2">
                                                        {f.replace(/_/g, " ")}
                                                    </label>
                                                    <input
                                                        data-testid={`provider-cred-${f}`}
                                                        type={f.includes("token") || f.includes("secret") ? "password" : "text"}
                                                        value={providerCreds[f] || ""}
                                                        onFocus={(e) => {
                                                            // Clear bullet placeholder on focus so admin can type fresh.
                                                            if (e.target.value === MASK) {
                                                                setProviderCreds((c) => ({ ...c, [f]: "" }));
                                                            }
                                                        }}
                                                        onChange={(e) =>
                                                            setProviderCreds((c) => ({ ...c, [f]: e.target.value }))
                                                        }
                                                        className="mt-2 w-full rounded-lg border border-stone-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-ink focus:ring-2 focus:ring-brand/30"
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}
                                {(PROVIDER_FIELDS[providerName] || []).length > 0 && (
                                    <div className="mt-5">
                                        <button
                                            data-testid="save-provider-creds"
                                            onClick={saveProviderCreds}
                                            className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 font-heading text-sm font-semibold text-white hover:-translate-y-0.5"
                                        >
                                            <FloppyDisk size={16} weight="fill" /> Save credentials
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </TabsContent>

                    {/* Caller IDs */}
                    <TabsContent value="caller-ids">
                        <div className="space-y-4 rounded-2xl border border-stone-200 bg-white p-6 sm:p-8">
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-stone2">Caller IDs</div>
                                    <p className="text-sm text-stone2">Outbound numbers your agents can dial from.</p>
                                </div>
                                <button
                                    data-testid="add-caller-id"
                                    onClick={addCallerId}
                                    className="inline-flex items-center gap-2 rounded-full border border-stone-300 bg-white px-4 py-2 text-sm font-semibold hover:border-ink"
                                >
                                    <Plus size={14} /> Add
                                </button>
                            </div>
                            {(settings.active_provider === "plivo" || settings.active_provider === "exotel") && (
                                <div data-testid="caller-id-provider-note" className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2.5 text-xs leading-relaxed text-sky-800">
                                    <b>{settings.active_provider === "plivo" ? "Plivo" : "Exotel"} caller ID:</b> outbound calls automatically use your {settings.active_provider === "plivo" ? "Plivo" : "Exotel"} voice number
                                    {providerCreds.voice_number ? <> (<span className="font-mono">{providerCreds.voice_number}</span>)</> : " (set it in the Provider tab)"} as the caller ID, so a number from another provider is never sent.
                                    Indian numbers must be rented/verified on your {settings.active_provider === "plivo" ? "Plivo" : "Exotel"} account and entered in full E.164 format, e.g. <span className="font-mono">+9122XXXXXXXX</span>.
                                </div>
                            )}

                            <div className="space-y-3">
                                {callerIds.length === 0 && (
                                    <div className="rounded-lg border border-dashed border-stone-300 bg-stone-50 p-6 text-center text-sm text-stone2">
                                        No caller IDs yet. Add your first one above.
                                    </div>
                                )}
                                {callerIds.map((c, i) => (
                                    <div key={i} className="grid grid-cols-12 items-center gap-3 rounded-xl border border-stone-200 bg-white p-3">
                                        <input
                                            data-testid={`caller-label-${i}`}
                                            value={c.label}
                                            onChange={(e) => updateCaller(i, "label", e.target.value)}
                                            placeholder="Label (e.g. Sales US)"
                                            className="col-span-4 rounded-lg border border-stone-200 px-3 py-2 text-sm outline-none focus:border-ink"
                                        />
                                        <input
                                            data-testid={`caller-number-${i}`}
                                            value={c.number}
                                            onChange={(e) => updateCaller(i, "number", e.target.value)}
                                            placeholder="+15555550100"
                                            className="col-span-5 rounded-lg border border-stone-200 px-3 py-2 text-sm outline-none focus:border-ink tabular"
                                        />
                                        <button
                                            data-testid={`caller-default-${i}`}
                                            onClick={() => setDefaultCaller(i)}
                                            className={`col-span-2 rounded-full px-3 py-2 text-xs font-bold uppercase tracking-widest ${
                                                c.is_default ? "bg-emerald-500 text-white" : "border border-stone-300 bg-white text-stone2 hover:border-ink"
                                            }`}
                                        >
                                            {c.is_default ? "Default" : "Set default"}
                                        </button>
                                        <button
                                            data-testid={`caller-remove-${i}`}
                                            onClick={() => removeCaller(i)}
                                            className="col-span-1 grid place-items-center rounded-lg p-2 text-stone2 hover:bg-red-50 hover:text-red-600"
                                        >
                                            <Trash size={16} />
                                        </button>
                                    </div>
                                ))}
                            </div>

                            <button
                                data-testid="save-caller-ids"
                                onClick={saveCallerIds}
                                className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 font-heading text-sm font-semibold text-white hover:-translate-y-0.5"
                            >
                                <FloppyDisk size={16} weight="fill" /> Save caller IDs
                            </button>
                        </div>
                    </TabsContent>

                    {/* Team */}
                    <TabsContent value="team">
                        <div className="mb-6 space-y-6 rounded-2xl border border-stone-200 bg-white p-6 sm:p-8" data-testid="routing-panel">
                            {callCenter && (<>
                            <div>
                                <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-stone2">Inbound call routing</div>
                                <p className="mt-1 text-sm text-stone2">How incoming calls are distributed across your online agents.</p>
                            </div>
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                <button
                                    data-testid="routing-simultaneous"
                                    onClick={() => setRouting({ ...routing, strategy: "simultaneous" })}
                                    className={`rounded-2xl border-2 p-4 text-left transition-all ${routing.strategy === "simultaneous" ? "border-ink bg-stone-50" : "border-stone-200 hover:border-stone-300"}`}
                                >
                                    <div className="font-heading text-sm font-bold">Ring All (Simultaneous)</div>
                                    <p className="mt-1 text-xs text-stone2">Every online agent rings at once — first to answer takes the call.</p>
                                </button>
                                <button
                                    data-testid="routing-round-robin"
                                    onClick={() => setRouting({ ...routing, strategy: "round_robin" })}
                                    className={`rounded-2xl border-2 p-4 text-left transition-all ${routing.strategy === "round_robin" ? "border-ink bg-stone-50" : "border-stone-200 hover:border-stone-300"}`}
                                >
                                    <div className="font-heading text-sm font-bold">Round Robin</div>
                                    <p className="mt-1 text-xs text-stone2">Agents are rung one at a time in rotation, spreading calls evenly.</p>
                                </button>
                            </div>
                            <div className="flex flex-wrap items-end gap-4">
                                <div>
                                    <label className="text-xs font-bold uppercase tracking-widest text-stone2">Ring timeout (sec)</label>
                                    <input
                                        data-testid="routing-timeout"
                                        type="number"
                                        min={10}
                                        max={120}
                                        value={routing.ring_timeout}
                                        onChange={(e) => setRouting({ ...routing, ring_timeout: parseInt(e.target.value || "25", 10) })}
                                        className="mt-2 w-28 rounded-lg border border-stone-300 px-3 py-2.5 text-sm outline-none focus:border-ink"
                                    />
                                </div>
                                <div className="min-w-[260px] flex-1">
                                    <label className="text-xs font-bold uppercase tracking-widest text-stone2">Transfer hold music (MP3 URL)</label>
                                    <input
                                        data-testid="routing-hold-music"
                                        type="url"
                                        placeholder="https://…/music.mp3"
                                        value={routing.hold_music_url || ""}
                                        onChange={(e) => setRouting({ ...routing, hold_music_url: e.target.value })}
                                        className="mt-2 w-full rounded-lg border border-stone-300 px-3 py-2.5 text-sm outline-none focus:border-ink"
                                    />
                                    <p className="mt-1.5 text-[11px] text-stone2">
                                        Played to the customer while a transfer connects. Paste any public MP3 URL. Leave blank for the default lively track.
                                    </p>
                                </div>
                                <button
                                    data-testid="routing-save"
                                    onClick={saveRouting}
                                    disabled={routingBusy}
                                    className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 font-heading text-sm font-semibold text-white hover:-translate-y-0.5 disabled:opacity-50"
                                >
                                    <FloppyDisk size={16} weight="fill" /> Save routing
                                </button>
                            </div>
                            </>)}

                            <div className={callCenter ? "border-t border-stone-200 pt-5" : ""}>
                                <div className="mb-3 text-[11px] font-bold uppercase tracking-[0.22em] text-stone2">Team availability</div>
                                <div className="space-y-2" data-testid="team-presence">
                                    {presence.map((a) => {
                                        const dot = a.status === "online" ? "bg-emerald-500" : a.status === "break" ? "bg-amber-500" : "bg-stone-300";
                                        return (
                                            <div key={a.id} className="flex items-center gap-3 rounded-xl border border-stone-100 bg-stone-50 px-3 py-2">
                                                <span className={`h-2.5 w-2.5 rounded-full ${dot}`} />
                                                <span className="flex-1 text-sm font-medium">{a.name}</span>
                                                <span className="text-[11px] uppercase tracking-widest text-stone2">{a.status}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        <div className="space-y-6 rounded-2xl border border-stone-200 bg-white p-6 sm:p-8">
                            <div>
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-stone2">Invite team member</div>
                                    {seatInfo?.user_cap != null && (
                                        <span
                                            data-testid="seat-usage"
                                            className={`rounded-full px-2.5 py-1 text-xs font-semibold ${agents.length >= seatInfo.user_cap ? "bg-red-100 text-red-700" : "bg-stone-100 text-stone-600"}`}
                                        >
                                            {agents.length} / {seatInfo.user_cap} seats used
                                        </span>
                                    )}
                                </div>
                                <p className="mt-1 text-sm text-stone2">We'll email them a secure link to set their own password and join your workspace — no temporary passwords to share.</p>
                                {seatInfo?.user_cap != null && agents.length >= seatInfo.user_cap && (
                                    <div data-testid="seat-cap-banner" className="mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                                        <span>You've used all {seatInfo.user_cap} seats on your {seatInfo.plan_name} plan. Add more seats to invite additional members.</span>
                                        <a href="/billing" data-testid="seat-cap-upgrade" className="ml-auto rounded-full bg-brand px-3 py-1.5 text-xs font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-brand-hover">Add seats</a>
                                    </div>
                                )}
                                <form onSubmit={createAgent} className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-12">
                                    <input
                                        data-testid="agent-name-input"
                                        required
                                        value={newAgent.name}
                                        onChange={(e) => setNewAgent({ ...newAgent, name: e.target.value })}
                                        placeholder="Member name"
                                        className="sm:col-span-4 rounded-lg border border-stone-300 px-3 py-2.5 text-sm outline-none focus:border-ink"
                                    />
                                    <input
                                        data-testid="agent-email-input"
                                        required
                                        type="email"
                                        value={newAgent.email}
                                        onChange={(e) => setNewAgent({ ...newAgent, email: e.target.value })}
                                        placeholder="member@company.com"
                                        className="sm:col-span-4 rounded-lg border border-stone-300 px-3 py-2.5 text-sm outline-none focus:border-ink"
                                    />
                                    <select
                                        data-testid="agent-role-select"
                                        value={newAgent.role}
                                        onChange={(e) => setNewAgent({ ...newAgent, role: e.target.value })}
                                        className="sm:col-span-2 rounded-lg border border-stone-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-ink"
                                    >
                                        <option value="agent">Agent</option>
                                        <option value="supervisor">Supervisor</option>
                                    </select>
                                    <button
                                        data-testid="create-agent-button"
                                        disabled={busyAgent || (seatInfo?.user_cap != null && agents.length >= seatInfo.user_cap)}
                                        className="sm:col-span-2 inline-flex items-center justify-center gap-2 rounded-full bg-brand px-4 py-2.5 font-heading text-sm font-semibold text-white hover:-translate-y-0.5 hover:bg-brand-hover disabled:opacity-50 disabled:hover:translate-y-0"
                                    >
                                        <UserPlus size={14} weight="fill" /> {busyAgent ? "Sending…" : "Send invite"}
                                    </button>
                                </form>

                                {inviteResult && (
                                    <div data-testid="invite-result" className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50/70 p-4">
                                        <div className="flex items-center gap-2 text-sm font-semibold text-emerald-800">
                                            <CheckCircle size={16} weight="fill" />
                                            {inviteResult.email_sent
                                                ? `Invitation emailed to ${inviteResult.email}`
                                                : `Invite ready for ${inviteResult.email}`}
                                        </div>
                                        {!inviteResult.email_sent && (
                                            <>
                                                <p className="mt-1 text-xs text-emerald-700/80">Auto-email didn't go out, so share this secure link with {inviteResult.name} directly — they can accept and log in right away. It expires in 7 days.</p>
                                                {inviteResult.email_error && (
                                                    <p data-testid="invite-email-error" className="mt-1 text-xs text-amber-700"><b>Email reason:</b> {inviteResult.email_error}</p>
                                                )}
                                            </>
                                        )}
                                        <div className="mt-3 flex items-center gap-2">
                                            <input
                                                data-testid="invite-link-field"
                                                readOnly
                                                value={inviteResult.link}
                                                onFocus={(e) => e.target.select()}
                                                className="flex-1 rounded-lg border border-emerald-200 bg-white px-3 py-2 text-xs text-stone-700 outline-none"
                                            />
                                            <button
                                                data-testid="copy-invite-link"
                                                type="button"
                                                onClick={() => copyInvite(inviteResult.link)}
                                                className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-white px-3 py-2 text-xs font-semibold text-emerald-800 hover:bg-emerald-100"
                                            >
                                                <Copy size={14} /> Copy link
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="border-t border-stone-200 pt-6">
                                <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-stone2 mb-3">Team ({agents.length})</div>
                                <div className="overflow-hidden rounded-xl border border-stone-200">
                                    <table className="w-full text-left">
                                        <thead className="bg-stone-50/70">
                                            <tr>
                                                {["Name", "Email", "Role", "Joined", "Actions"].map((h) => (
                                                    <th key={h} className="px-4 py-2 text-[10px] font-bold uppercase tracking-[0.18em] text-stone2">{h}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {agents.map((a) => {
                                                const isSelf = a.id === currentUser?.id;
                                                const roleStyle = a.role === "admin"
                                                    ? "bg-amber-100 text-amber-800"
                                                    : a.role === "supervisor"
                                                        ? "bg-[#00A1E0]/10 text-[#00A1E0]"
                                                        : "bg-stone-100 text-stone2";
                                                return (
                                                <tr key={a.id} className="border-t border-stone-100" data-testid={`agent-row-${a.id}`}>
                                                    <td className="px-4 py-3 text-sm font-medium">
                                                        {a.name}{isSelf && <span className="ml-1 text-[10px] text-stone2">(you)</span>}
                                                        {a.status === "invited" && (
                                                            <span data-testid={`pending-badge-${a.id}`} className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-amber-700">Pending</span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-stone2">{a.email}</td>
                                                    <td className="px-4 py-3">
                                                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${roleStyle}`} data-testid={`role-badge-${a.id}`}>
                                                            {a.role === "admin" && <Crown size={10} weight="fill" />}
                                                            {a.role === "supervisor" && <ShieldCheck size={10} weight="fill" />}
                                                            {a.role}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 text-xs text-stone2">
                                                        {a.created_at ? new Date(a.created_at).toLocaleDateString() : "—"}
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        {isSelf ? (
                                                            <span className="text-xs text-stone-300">—</span>
                                                        ) : (
                                                            <div className="flex items-center gap-2">
                                                                {a.status === "invited" && (
                                                                    <button
                                                                        data-testid={`resend-invite-${a.id}`}
                                                                        onClick={() => resendInvite(a)}
                                                                        title="Resend invite"
                                                                        className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 px-2 py-1.5 text-xs font-semibold text-emerald-700 transition-all hover:bg-emerald-50"
                                                                    >
                                                                        <UserPlus size={13} /> Resend
                                                                    </button>
                                                                )}
                                                                <select
                                                                    data-testid={`change-role-${a.id}`}
                                                                    value={a.role}
                                                                    onChange={(e) => changeRole(a.id, e.target.value)}
                                                                    className="rounded-lg border border-stone-300 bg-white px-2 py-1.5 text-xs outline-none focus:border-ink"
                                                                >
                                                                    <option value="agent">Agent</option>
                                                                    <option value="supervisor">Supervisor</option>
                                                                    <option value="admin">Admin</option>
                                                                </select>
                                                                <button
                                                                    data-testid={`remove-member-${a.id}`}
                                                                    onClick={() => removeMember(a)}
                                                                    title="Remove member"
                                                                    className="grid h-8 w-8 place-items-center rounded-lg border border-red-200 text-red-500 transition-all hover:bg-red-50"
                                                                >
                                                                    <Trash size={14} />
                                                                </button>
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </TabsContent>

                    {/* Salesforce */}
                    {/* Salesforce */}
                    <TabsContent value="queues">
                        <CallQueues />
                    </TabsContent>

                    <TabsContent value="salesforce">
                        <div className="space-y-6 rounded-2xl border border-stone-200 bg-white p-6 sm:p-8" data-testid="salesforce-panel">
                            {/* Status header */}
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="grid h-10 w-10 place-items-center overflow-hidden rounded-xl bg-[#00A1E0]/10 text-[#00A1E0]">
                                        {settings.sf_logo ? (
                                            <img src={settings.sf_logo} alt="Salesforce" data-testid="sf-logo-img" className="h-full w-full object-contain" />
                                        ) : (
                                            <Cloud size={22} weight="fill" />
                                        )}
                                    </div>
                                    <div>
                                        <div className="font-heading text-lg font-bold leading-none">Salesforce CRM</div>
                                        <div className="mt-1 text-xs text-stone2">{callCenter ? "Embedded softphone, auto call logging & click-to-call." : "Sync contacts & leads for WhatsApp broadcasts and chat screen-pop."}</div>
                                        <div className="mt-1.5 flex items-center gap-2">
                                            <label data-testid="sf-logo-upload" className="inline-flex cursor-pointer items-center gap-1 rounded-full border border-stone-300 bg-white px-2.5 py-1 text-[11px] font-semibold text-stone-600 transition-colors hover:border-ink">
                                                <UploadSimple size={12} /> Upload logo
                                                <input type="file" accept="image/*" className="hidden" onChange={(e) => { uploadSfLogo(e.target.files?.[0]); e.target.value = ""; }} />
                                            </label>
                                            {settings.sf_logo && (
                                                <button data-testid="sf-logo-reset" type="button" onClick={resetSfLogo} className="text-[11px] font-semibold text-stone-400 transition-colors hover:text-red-500">Reset</button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                {sf?.connected ? (
                                    <span data-testid="sf-status-connected" className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-emerald-700">
                                        <PlugsConnected size={13} weight="fill" /> Connected
                                    </span>
                                ) : (
                                    <span data-testid="sf-status-disconnected" className="inline-flex items-center gap-1.5 rounded-full bg-stone-100 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-stone2">
                                        <Plugs size={13} /> Not connected
                                    </span>
                                )}
                            </div>

                            {/* Step 1 — Connected App keys */}
                            <div className="border-t border-stone-200 pt-6">
                                <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-stone2">1 · Connected App keys</div>
                                <p className="mt-1 mb-3 text-xs text-stone2">
                                    In Salesforce: Setup → App Manager → New Connected App. Enable OAuth, add the callback URL below,
                                    and select scopes <code>api</code> and <code>refresh_token</code>. Paste the generated Consumer Key & Secret here.
                                </p>
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    <div>
                                        <label className="text-xs font-bold uppercase tracking-widest text-stone2">Consumer Key {sf?.configured && <span className="text-emerald-600">· saved</span>}</label>
                                        <input
                                            data-testid="sf-client-id"
                                            type="text"
                                            value={sfForm.client_id}
                                            onChange={(e) => setSfForm({ ...sfForm, client_id: e.target.value })}
                                            placeholder={sf?.configured ? "••• (leave blank to keep)" : "3MVG9..."}
                                            className="mt-2 w-full rounded-lg border border-stone-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-ink focus:ring-2 focus:ring-brand/30"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs font-bold uppercase tracking-widest text-stone2">Consumer Secret</label>
                                        <input
                                            data-testid="sf-client-secret"
                                            type="password"
                                            value={sfForm.client_secret}
                                            onChange={(e) => setSfForm({ ...sfForm, client_secret: e.target.value })}
                                            placeholder={sf?.configured ? "••• (leave blank to keep)" : "secret"}
                                            className="mt-2 w-full rounded-lg border border-stone-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-ink focus:ring-2 focus:ring-brand/30"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs font-bold uppercase tracking-widest text-stone2">Environment</label>
                                        <select
                                            data-testid="sf-env"
                                            value={sfForm.env}
                                            onChange={(e) => setSfForm({ ...sfForm, env: e.target.value })}
                                            className="mt-2 w-full rounded-lg border border-stone-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-ink"
                                        >
                                            <option value="production">Production (login.salesforce.com)</option>
                                            <option value="sandbox">Sandbox (test.salesforce.com)</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="text-xs font-bold uppercase tracking-widest text-stone2">My Domain <span className="text-stone-400">(recommended)</span></label>
                                        <input
                                            data-testid="sf-domain"
                                            type="text"
                                            value={sfForm.domain}
                                            onChange={(e) => setSfForm({ ...sfForm, domain: e.target.value })}
                                            placeholder="mycompany.my.salesforce.com"
                                            className="mt-2 w-full rounded-lg border border-stone-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-ink focus:ring-2 focus:ring-brand/30"
                                        />
                                        <p className="mt-1 text-[11px] text-stone2">Required for new “External Client Apps”. Find it in Setup → My Domain (paste the host only, no https://).</p>
                                    </div>
                                </div>
                                <div className="mt-3 flex items-center gap-2 rounded-lg border border-stone-200 bg-stone-50 px-3 py-2 font-mono text-xs">
                                    <span className="text-stone2">Callback URL:</span>
                                    <code className="flex-1 truncate" data-testid="sf-callback-url">{callbackUrl}</code>
                                    <button
                                        onClick={() => { Promise.resolve(navigator.clipboard?.writeText(callbackUrl)).catch(() => {}); toast.success("Copied"); }}
                                        className="rounded-md bg-ink px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-white"
                                    >
                                        <Copy size={11} />
                                    </button>
                                </div>
                                <div data-testid="sf-callback-help" className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-800">
                                    <b>Important — avoids “redirect_uri_mismatch”:</b> paste this <b>exact</b> Callback URL into your Salesforce Connected App
                                    (<span className="font-mono">Setup → App Manager → your app → Edit → Callback URL</span>), then save and wait ~2–10 min.
                                    If you use the app from more than one domain (e.g. preview <i>and</i> production), add each callback URL on its own line — Salesforce allows multiple.
                                </div>
                                <div className="mt-4">
                                    <button
                                        data-testid="sf-save-config"
                                        onClick={saveSfConfig}
                                        disabled={sfBusy}
                                        className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 font-heading text-sm font-semibold text-white hover:-translate-y-0.5 disabled:opacity-50"
                                    >
                                        <FloppyDisk size={16} weight="fill" /> Save keys
                                    </button>
                                </div>
                            </div>

                            {/* Step 2 — Connect */}
                            <div className="border-t border-stone-200 pt-6">
                                <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-stone2">2 · Authorize</div>
                                <p className="mt-1 mb-3 text-xs text-stone2">Log into your Salesforce org and grant access. Tokens are encrypted at rest.</p>
                                <div className="flex flex-wrap items-center gap-3">
                                    {!sf?.connected ? (
                                        <button
                                            data-testid="sf-connect"
                                            onClick={connectSf}
                                            disabled={!sf?.configured}
                                            className="inline-flex items-center gap-2 rounded-full bg-[#00A1E0] px-5 py-2.5 font-heading text-sm font-semibold text-white hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                            <PlugsConnected size={16} weight="fill" /> Connect Salesforce
                                        </button>
                                    ) : (
                                        <>
                                            <button
                                                data-testid="sf-test"
                                                onClick={testSf}
                                                disabled={sfBusy}
                                                className="inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 font-heading text-sm font-semibold text-white hover:-translate-y-0.5 hover:bg-brand-hover disabled:opacity-50"
                                            >
                                                <CheckCircle size={16} weight="fill" /> Test connection
                                            </button>
                                            <button
                                                data-testid="sf-disconnect"
                                                onClick={disconnectSf}
                                                className="inline-flex items-center gap-2 rounded-full border border-stone-300 bg-white px-5 py-2.5 font-heading text-sm font-semibold text-stone2 hover:border-red-400 hover:text-red-600"
                                            >
                                                <Plugs size={16} /> Disconnect
                                            </button>
                                        </>
                                    )}
                                    {sf?.connected && sf?.instance_url && (
                                        <span className="text-xs text-stone2">Org: <code className="font-mono">{sf.instance_url}</code></span>
                                    )}
                                </div>
                            </div>

                            {/* Steps 3–5 are call-center specific (call logging, softphone, call field mapping) */}
                            {callCenter && (<>
                            {/* Step 3 — Call logging toggle */}
                            <div className="border-t border-stone-200 pt-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-stone2">3 · Auto-log calls</div>
                                        <p className="mt-1 text-sm text-stone2">Create a Salesforce Task on the matching Contact/Lead when a call ends.</p>
                                    </div>
                                    <Switch
                                        data-testid="sf-log-toggle"
                                        checked={!!sf?.log_calls}
                                        onCheckedChange={toggleSfLog}
                                    />
                                </div>
                            </div>

                            {/* Step 3.5 — Salesforce user mapping */}
                            {sf?.connected && (
                                <div className="border-t border-stone-200 pt-6" data-testid="sf-user-mapping">
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-stone2">3b · Agent → Salesforce user mapping</div>
                                        <button data-testid="refresh-user-mappings" onClick={refreshSfUsers} disabled={sfMapBusy}
                                            title="Re-check every agent against the currently connected Salesforce org (use after reconnecting a new org)"
                                            className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 px-3 py-1 text-[11px] font-semibold hover:border-ink disabled:opacity-50">
                                            <ArrowClockwise size={12} className={sfMapBusy ? "animate-spin" : ""} /> Refresh users
                                        </button>
                                    </div>
                                    <p className="mt-1 mb-4 text-sm text-stone2">
                                        Each call is logged under the Salesforce user whose email matches the Inspiro agent. If your
                                        Salesforce email differs, enter it as an override. Unmatched agents fall back to the connected identity.
                                        After reconnecting a new org, click <b>Refresh users</b> to clear stale matches.
                                    </p>
                                    <div className="overflow-hidden rounded-xl border border-stone-200">
                                        <table className="w-full text-sm">
                                            <thead className="bg-stone-50 text-left text-[11px] uppercase tracking-widest text-stone2">
                                                <tr>
                                                    <th className="px-4 py-2.5 font-semibold">Agent</th>
                                                    <th className="px-4 py-2.5 font-semibold">Salesforce email (override)</th>
                                                    <th className="px-4 py-2.5 font-semibold">Status</th>
                                                    <th className="px-4 py-2.5" />
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-stone-100">
                                                {sfMappings.map((m) => (
                                                    <tr key={m.user_id} data-testid={`sf-map-row-${m.user_id}`}>
                                                        <td className="px-4 py-3">
                                                            <div className="font-semibold">{m.name}</div>
                                                            <div className="text-[11px] text-stone2">{m.email}</div>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <input
                                                                data-testid={`sf-map-input-${m.user_id}`}
                                                                value={sfMapDraft[m.user_id] ?? ""}
                                                                onChange={(e) => setSfMapDraft((d) => ({ ...d, [m.user_id]: e.target.value }))}
                                                                placeholder={m.email}
                                                                className="w-full rounded-lg border border-stone-200 px-3 py-1.5 text-sm focus:border-ink focus:outline-none"
                                                            />
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            {m.matched ? (
                                                                <span data-testid={`sf-map-status-${m.user_id}`} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-emerald-700">
                                                                    <CheckCircle size={12} weight="fill" /> {m.sf_user_name || "Mapped"}
                                                                </span>
                                                            ) : (
                                                                <span data-testid={`sf-map-status-${m.user_id}`} className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-amber-700">
                                                                    Unmapped
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-3 text-right">
                                                            <button
                                                                data-testid={`sf-map-save-${m.user_id}`}
                                                                onClick={() => saveSfMapping(m.user_id)}
                                                                disabled={sfMapBusy}
                                                                className="rounded-full bg-ink px-3 py-1.5 text-xs font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                                                            >
                                                                Save
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* Step 4 — Embedded softphone */}
                            <div className="border-t border-stone-200 pt-6">
                                <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-stone2">4 · Embedded softphone (Open CTI)</div>
                                <p className="mt-1 mb-3 text-xs text-stone2">
                                    Download the Call Center file, import it in Salesforce (Setup → Call Centers → Import), assign your users,
                                    then add the “Open CTI Softphone” utility to your Lightning app. Click-to-call works automatically once assigned.
                                </p>
                                <div className="flex flex-wrap items-center gap-3">
                                    <button
                                        type="button"
                                        data-testid="sf-download-xml"
                                        onClick={downloadCallCenterXml}
                                        className="inline-flex items-center gap-2 rounded-full border border-stone-300 bg-white px-5 py-2.5 font-heading text-sm font-semibold hover:border-ink"
                                    >
                                        <Download size={16} /> Download Call Center XML
                                    </button>
                                    <a
                                        href={`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "")}/salesforce/softphone`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center gap-2 text-xs font-semibold text-brand hover:text-brand-hover"
                                    >
                                        <ArrowSquareOut size={14} /> Preview adapter page
                                    </a>
                                </div>
                            </div>

                            {/* Step 5 — Field mapping */}
                            <div className="rounded-2xl border border-stone-200 bg-white p-5">
                                <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-stone2">5 · Call field mapping</div>
                                <p className="mt-1 mb-4 text-sm text-stone2">
                                    Choose which Salesforce fields each call detail is written to. Create the custom
                                    fields (and optional custom object) in Salesforce first, then map them here.
                                </p>
                                <SalesforceFieldMapping sf={sf} onSaved={setSf} />
                            </div>
                            </>)}
                        </div>
                    </TabsContent>

                    {/* General */}
                    <TabsContent value="general">
                        <div className="space-y-6 rounded-2xl border border-stone-200 bg-white p-6 sm:p-8">
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-stone2">Outbound Call Recording</div>
                                    <p className="mt-1 text-sm text-stone2">When enabled, outbound calls are recorded (provider must support recording).</p>
                                </div>
                                <Switch
                                    data-testid="recording-toggle"
                                    checked={settings.recording_enabled}
                                    onCheckedChange={toggleRecording}
                                />
                            </div>
                            <div className="flex items-center justify-between border-t border-stone-200 pt-6">
                                <div>
                                    <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-stone2">Inbound Call Recording</div>
                                    <p className="mt-1 text-sm text-stone2">When enabled, incoming calls answered by an agent are recorded.</p>
                                </div>
                                <Switch
                                    data-testid="inbound-recording-toggle"
                                    checked={settings.inbound_recording_enabled ?? settings.recording_enabled}
                                    onCheckedChange={toggleInboundRecording}
                                />
                            </div>
                            {settings.active_provider === "twilio" && (<>
                            <div className="border-t border-stone-200 pt-6">
                                <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-stone2">
                                    <PhoneIncoming size={12} weight="fill" className="inline mr-1" /> Inbound webhook (Twilio)
                                </div>
                                <p className="mt-1 text-sm text-stone2">
                                    Set this URL on your Twilio number's Voice "A Call Comes In" webhook to route incoming calls to your dialer. <b>This URL is unique to this workspace</b> (it carries your workspace ID), so calls always reach the right team even if another workspace uses Twilio too.
                                </p>
                                <div className="mt-3 flex items-center gap-2 rounded-lg border border-stone-200 bg-stone-50 px-3 py-2 font-mono text-xs">
                                    <code className="flex-1 truncate" data-testid="inbound-webhook-url">{incomingUrl}</code>
                                    <button
                                        onClick={() => { navigator.clipboard.writeText(incomingUrl); toast.success("Copied"); }}
                                        className="rounded-md bg-ink px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-white"
                                    >
                                        Copy
                                    </button>
                                </div>
                            </div>
                            <div className="border-t border-stone-200 pt-6">
                                <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-stone2">
                                    <PhoneIncoming size={12} weight="fill" className="inline mr-1 rotate-180" /> Outbound TwiML App Voice URL
                                </div>
                                <p className="mt-1 text-sm text-stone2">
                                    Set this as the <b>Voice → Request URL (HTTP POST)</b> on your Twilio <b>TwiML App</b> (the one whose SID you entered as <code>twiml_app_sid</code>). Browser (outbound) calls fail with a gateway HANGUP (error 31005) if this points to the wrong environment.
                                </p>
                                <div className="mt-3 flex items-center gap-2 rounded-lg border border-stone-200 bg-stone-50 px-3 py-2 font-mono text-xs">
                                    <code className="flex-1 truncate" data-testid="outbound-voice-url">{outboundVoiceUrl}</code>
                                    <button
                                        onClick={() => { navigator.clipboard.writeText(outboundVoiceUrl); toast.success("Copied"); }}
                                        data-testid="outbound-voice-url-copy"
                                        className="rounded-md bg-ink px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-white"
                                    >
                                        Copy
                                    </button>
                                </div>
                            </div>
                            </>)}
                            {settings.active_provider === "plivo" && (
                            <div className="border-t border-stone-200 pt-6">
                                <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-stone2">
                                    <PhoneIncoming size={12} weight="fill" className="inline mr-1" /> Inbound webhook (Plivo)
                                </div>
                                <p className="mt-1 text-sm text-stone2">
                                    <b>Set automatically</b> — Inspiro provisions this Answer URL on your Plivo Application and points your Plivo number at it whenever you open the app, so you don't need to configure anything in the Plivo console. It's shown here for reference. If inbound stops working after switching environments, just reload this page to re-sync.
                                </p>
                                <div className="mt-3 flex items-center gap-2 rounded-lg border border-stone-200 bg-stone-50 px-3 py-2 font-mono text-xs">
                                    <code className="flex-1 truncate" data-testid="plivo-answer-url">{plivoAnswerUrl}</code>
                                    <button
                                        onClick={() => { navigator.clipboard.writeText(plivoAnswerUrl); toast.success("Copied"); }}
                                        data-testid="plivo-answer-url-copy"
                                        className="rounded-md bg-ink px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-white"
                                    >
                                        Copy
                                    </button>
                                </div>
                            </div>
                            )}
                            {settings.active_provider === "exotel" && (
                            <div className="border-t border-stone-200 pt-6">
                                <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-stone2">
                                    <PhoneIncoming size={12} weight="fill" className="inline mr-1" /> Inbound webhook (Exotel)
                                </div>
                                <p className="mt-1 text-sm text-stone2">
                                    In your Exotel dashboard, point your inbound App/number's <b>"Passthru" / connect URL</b> at this address so incoming calls route to your dialer. <b>This URL is unique to this workspace</b> (it carries your workspace ID).
                                </p>
                                <div className="mt-3 flex items-center gap-2 rounded-lg border border-stone-200 bg-stone-50 px-3 py-2 font-mono text-xs">
                                    <code className="flex-1 truncate" data-testid="exotel-incoming-url">{exotelIncomingUrl}</code>
                                    <button
                                        onClick={() => { navigator.clipboard.writeText(exotelIncomingUrl); toast.success("Copied"); }}
                                        data-testid="exotel-incoming-url-copy"
                                        className="rounded-md bg-ink px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-white"
                                    >
                                        Copy
                                    </button>
                                </div>
                            </div>
                            )}
                            <div className="border-t border-stone-200 pt-6">
                                <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-stone2">Timezone</div>
                                <p className="mt-1 text-sm text-stone2">Dates and times across the app (call history, reports, call details) display in this timezone.</p>
                                <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
                                    <select
                                        data-testid="timezone-select"
                                        value={tz}
                                        onChange={(e) => setTz(e.target.value)}
                                        className="w-full rounded-lg border border-stone-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-ink sm:max-w-xs"
                                    >
                                        <option value="">Auto — device timezone ({browserTimezone()})</option>
                                        {timezoneList().map((z) => (
                                            <option key={z} value={z}>{z}</option>
                                        ))}
                                    </select>
                                    <button
                                        onClick={saveTimezone}
                                        disabled={tzBusy}
                                        data-testid="save-timezone"
                                        className="inline-flex items-center justify-center gap-1.5 rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 disabled:opacity-50"
                                    >
                                        <FloppyDisk size={16} /> {tzBusy ? "Saving…" : "Save"}
                                    </button>
                                </div>
                            </div>
                            <div className="border-t border-stone-200 pt-6">
                                <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-stone2">Default Dialing Country</div>
                                <p className="mt-1 text-sm text-stone2">Numbers typed without a country code are auto-formatted to E.164 using this country (e.g. a 10-digit Indian number becomes +91…). Numbers already starting with + are left as-is.</p>
                                <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
                                    <select
                                        data-testid="country-select"
                                        value={country}
                                        onChange={(e) => setCountry(e.target.value)}
                                        className="w-full rounded-lg border border-stone-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-ink sm:max-w-xs"
                                    >
                                        {DIAL_COUNTRIES.map((c) => (
                                            <option key={c.code} value={c.code}>{c.name}</option>
                                        ))}
                                    </select>
                                    <button
                                        onClick={saveCountry}
                                        disabled={countryBusy}
                                        data-testid="save-country"
                                        className="inline-flex items-center justify-center gap-1.5 rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 disabled:opacity-50"
                                    >
                                        <FloppyDisk size={16} /> {countryBusy ? "Saving…" : "Save"}
                                    </button>
                                </div>
                            </div>
                            <div className="border-t border-stone-200 pt-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-stone2">Business Hours</div>
                                        <p className="mt-1 text-sm text-stone2">Outside these hours, inbound callers hear your closed message and the call ends. Uses your workspace timezone.</p>
                                    </div>
                                    <Switch data-testid="bh-toggle" checked={bh.enabled} onCheckedChange={(v) => setBh((b) => ({ ...b, enabled: v }))} />
                                </div>
                                {bh.enabled && (
                                    <div className="mt-4 space-y-4" data-testid="bh-config">
                                        <div className="flex flex-wrap items-end gap-4">
                                            <div>
                                                <label className="text-[10px] font-bold uppercase tracking-widest text-stone2">Opens</label>
                                                <input type="time" data-testid="bh-open" value={bh.open} onChange={(e) => setBh((b) => ({ ...b, open: e.target.value }))} className="mt-1.5 block rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-ink" />
                                            </div>
                                            <div>
                                                <label className="text-[10px] font-bold uppercase tracking-widest text-stone2">Closes</label>
                                                <input type="time" data-testid="bh-close" value={bh.close} onChange={(e) => setBh((b) => ({ ...b, close: e.target.value }))} className="mt-1.5 block rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-ink" />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="text-[10px] font-bold uppercase tracking-widest text-stone2">Open days</label>
                                            <div className="mt-1.5 flex flex-wrap gap-2" data-testid="bh-days">
                                                {DAYS.map(([d, label]) => (
                                                    <button key={d} type="button" data-testid={`bh-day-${d}`} onClick={() => toggleBhDay(d)} className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-all ${bh.days.includes(d) ? "border-ink bg-ink text-white" : "border-stone-300 bg-white text-stone2 hover:border-ink"}`}>{label}</button>
                                                ))}
                                            </div>
                                        </div>
                                        <div>
                                            <label className="text-[10px] font-bold uppercase tracking-widest text-stone2">Closed message (spoken to callers)</label>
                                            <textarea data-testid="bh-message" value={bh.closed_message} onChange={(e) => setBh((b) => ({ ...b, closed_message: e.target.value }))} rows={2} placeholder="Thank you for calling. Our office is currently closed. Please call back during business hours." className="mt-1.5 w-full resize-none rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-ink" />
                                        </div>
                                    </div>
                                )}
                                <button onClick={saveBusinessHours} disabled={bhBusy} data-testid="save-business-hours" className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 disabled:opacity-50"><FloppyDisk size={16} /> {bhBusy ? "Saving…" : "Save business hours"}</button>
                            </div>
                            <div className="border-t border-stone-200 pt-6">
                                <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-stone2">Workspace</div>
                                <div className="mt-2 font-heading text-xl font-semibold">{settings.org_id}</div>
                                <p className="mt-1 text-xs text-stone2">Tenant ID — shared with all webhook events.</p>
                            </div>
                        </div>
                    </TabsContent>
                </Tabs>
            </div>
        </AppShell>
    );
}
